import 'swiper/css';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper as SwiperType } from 'swiper';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Flex } from '@monorepo/react-components';
import { IconsNames } from '@monorepo/types';
import BenefitCard from '../benefits/benefit-card';
import { getRandomMarkedIndexes } from '../utils';

import styles from './carousel.module.scss';

export enum CarouselDirection {
  LEFT = 'left',
  RIGHT = 'right',
}

interface Props {
  direction?: CarouselDirection;
}

const Carousel: FC<Props> = ({ direction = CarouselDirection.LEFT }) => {
  const { t } = useTranslation('affiliate');
  const swiperRef = useRef<SwiperType | null>(null);

  const benefitsMap = useMemo(
    () => [
      {
        icon: IconsNames.Rocket,
        title: t('welcome.benefits.affiliate_boost_kit'),
      },
      {
        icon: IconsNames.Diamond,
        title: t('welcome.benefits.eight_years_trusted'),
      },
      {
        icon: IconsNames.HeartHandshake,
        title: t('welcome.benefits.support'),
      },
      {
        icon: IconsNames.LayoutDashboard,
        title: t('welcome.benefits.advanced_dashboard'),
      },
      {
        icon: IconsNames.Sparkles,
        title: t('welcome.benefits.high_rewards'),
      },
    ],
    [t],
  );

  const extendedBenefitsMap = useMemo(
    () => [...benefitsMap, ...benefitsMap, ...benefitsMap],
    [benefitsMap],
  );
  const markedIndexes = getRandomMarkedIndexes(extendedBenefitsMap.length);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.autoplay.start();

      const swiperWrapper = swiperRef.current.wrapperEl;
      if (swiperWrapper) {
        swiperWrapper.style.transitionTimingFunction = 'linear';
      }
    }
  }, [swiperRef]);

  return (
    <div className={styles.marqueeWrapper}>
      <Swiper
        modules={[Autoplay]}
        spaceBetween={20}
        slidesPerView="auto"
        loop={true}
        speed={10000}
        autoplay={{
          delay: 1,
          disableOnInteraction: false,
          reverseDirection: direction === CarouselDirection.RIGHT,
        }}
        className={styles.marqueeContent}
        allowTouchMove={true}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onAutoplayPause={(swiper) => {
          swiper.autoplay.start();
        }}
      >
        {extendedBenefitsMap.map((benefit, index) => (
          <SwiperSlide key={index} className={styles.carouselItem}>
            <Flex>
              <BenefitCard
                benefit={benefit}
                index={index}
                isMarked={markedIndexes.has(index)}
              />
            </Flex>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default Carousel;
